import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import { EUserStatus } from '@/constants/userStatus'
import { useSSO } from '@/hooks/Auth'
import { useAppDispatch } from '@/hooks/redux'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { api } from '@/store/api'
import { useRegisterMutation } from '@/store/api/Auth/Auth.query'
import { setSession, setUserID } from '@/store/api/Auth/Auth.slice'
import { setUserOrigin } from '@/store/api/Users/Users.slice'
import { identifyUser } from '@/utils/analytics/analytics'
import { AuthMethods } from '@/utils/analytics/analyticsConstants'
import type { signUpType } from '@/utils/schemas'
import { signUpSchema } from '@/utils/schemas'

import GoogleOauthButton from '../Login/GoogleOauthButtom'

export const SignUpStart = () => {
  const dispatch = useAppDispatch()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const navigate = useNavigate()

  const [isChecked, setIsChecked] = useState(false)
  const { startGoogleSSO } = useSSO()

  const params = new URLSearchParams(location.search)
  const from = params.get('from')

  useEffect(() => {
    if (from) {
      dispatch(setUserOrigin(from))
    }
  }, [from])

  const form = useForm<signUpType>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      confirmPassword: '',
      createPassword: '',
    },
  })

  const {
    handleSubmit,
    control,
    formState: { errors = {}, dirtyFields },
    watch,
  } = form

  const passNotMatch =
    Object.keys(dirtyFields).includes('confirmPassword') &&
    watch().confirmPassword !== watch().createPassword

  const [register, { isLoading }] = useRegisterMutation()

  const handleCheckboxChange = (newChecked: boolean) => {
    setIsChecked(newChecked)
  }

  const makeRegister = async (password: string, email: string) => {
    try {
      const normalizedEmail = email.toLowerCase()

      const response = await register({
        email: normalizedEmail,
        password,
        language: language,
      }).unwrap()
      if (response.info.id) {
        dispatch(api.util.resetApiState())
        dispatch(setSession(response.info))
        dispatch(setUserID(response.info.id))

        // This identify user is for signup via email and password
        identifyUser(normalizedEmail, {
          user_id: response.info.id,
          email: normalizedEmail,
          user_status: EUserStatus.PENDING_EMAIL_VERIFICATION,
          method: AuthMethods.EMAIL,
        })

        navigate(AppRoutes.registerEmailVerification)
      }
    } catch (e) {
      catchWithSentry(e)
      if (e.data.code.includes('EMAIL_DUP')) {
        toast.error(t('signUp.emailRegistered'))
      } else if (e.data.code.includes('USER_HAS_INVITATION')) {
        toast.error(t('signUp.userHasInvitation'))
      } else {
        toast.error(t('signUp.error'))
      }
    }
  }

  const onSubmit = (data: any) => {
    if (passNotMatch) {
      return
    } else {
      makeRegister(data.createPassword, data.email)
    }
  }

  const handleGoogleSignup = () => {
    startGoogleSSO()
  }

  const handleBack = () => {
    navigate(AppRoutes.login)
  }

  return (
    <div className="w-full flex flex-col items-center justify-center gap-8">
      <div className="flex flex-col gap-8 max-w-[500px] bg-baseColorHighlight p-8 rounded-3xl">
        <div className="flex flex-col gap-4">
          <p className="text-[32px] font-medium text-neutralColor text-center">
            {t('signUp.title')}
          </p>
          <GoogleOauthButton variant="signup" onClick={handleGoogleSignup} />
        </div>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6 w-full"
          >
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem className="bg-white rounded-md">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input {...field} placeholder={t('signUp.email')} />
                  </FormControl>
                  <FormMessage data-name="email" />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="createPassword"
              render={({ field }) => (
                <FormItem className="w-full bg-white rounded-md">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input
                      {...field}
                      placeholder={t('signUp.createPassword')}
                      type="password"
                      showPassIcon
                    />
                  </FormControl>
                  <FormMessage data-name="createPassword" />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="w-full  bg-white rounded-md">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input
                      {...field}
                      placeholder={t('signUp.confirmPassword')}
                      type="password"
                      showPassIcon
                    />
                  </FormControl>
                  {passNotMatch && !errors.confirmPassword && (
                    <p className="text-[0.8rem] font-medium text-destructive">
                      {t('signUp.passwordsDoNotMatch')}
                    </p>
                  )}
                  <FormMessage data-name="confirmPassword" />
                </FormItem>
              )}
            />

            <div className="flex flex-row gap-3">
              <Checkbox
                className="w-5 h-5 rounded-md mt-1 border-neutralColor30"
                onCheckedChange={handleCheckboxChange}
              />
              <p className="text-textAccent text-base font-light ">
                {t('signUp.iAgree')}
              </p>
            </div>
            <Button
              disabled={!isChecked}
              type="submit"
              className="py-3 px-5 font-medium tracking-widest h-11 rounded-3xl"
              isLoading={isLoading}
            >
              {t('signUp.continue')}
            </Button>
          </form>
        </Form>

        <div className="border-t pt-6 border-neutralColor30">
          <p className="text-sm font-normal text-center">
            {t('signUp.clickingContinue')}&nbsp;
            <span
              className="text-linkColor text-nowrap"
              role="button"
              onClick={() =>
                window.open(t('common.termsAndConditionsLink'), '_blank')
              }
              tabIndex={0}
            >
              {t('signUp.termsAndConditions')}
            </span>
            &nbsp;{t('signUp.and')}&nbsp;
            <span
              className="text-linkColor"
              role="button"
              onClick={() =>
                window.open(t('common.privacyPolicyLink'), '_blank')
              }
              tabIndex={0}
            >
              {t('signUp.privacyPolicy')}.
            </span>
          </p>
        </div>
      </div>

      <div className="w-1/3">
        <button
          type="button"
          className="w-full text-center font-medium text-base text-neutralColor80 hover:text-neutralColor60 hover:cursor-pointer bg-transparent border-none"
          onClick={handleBack}
        >
          {t('common.back')}
        </button>
      </div>
    </div>
  )
}
